import type { BatchPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { Action } from 'state/actions';
import { addElements, replaceElements } from 'state/utils/array';
import { addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';
import { isExternalQuoteId } from 'utils/string';

import type {
  BatchTabContentFormDetails,
  BatchWorkspaceTabContent,
  BatchWorkspaceTabContentState,
} from './tabContent.model';
import { initialBatchWorkspaceTabContentState } from './tabContent.model';
export function batchWorkspaceTabContentReducer(
  state: BatchWorkspaceTabContentState = initialBatchWorkspaceTabContentState,
  action: Action,
): BatchWorkspaceTabContentState {
  switch (action.type) {
    case 'WORKSPACE_TAB_CREATED':
      if (action.workspace !== 'newBatch') {
        return state;
      }
      return addOrReplaceKey(state, action.id, {
        tabId: action.id,
        workspace: action.workspace,
        isFetchingTabContent: false,
        formIds: [],
        forms: [],
        isMultipleApplyOpened: false,
        selectedFieldsIds: [],
        isProductValidated: false,
        fields: {},
        updateActions: {},
        isDeleteFormConfirmationModalOpen: false,
      });
    case 'WORKSPACE_TAB_CONTENT_FETCH_REQUESTED':
      if (action.workspace !== 'newBatch') {
        return state;
      }
      return addOrReplaceKey(state, action.tabId, {
        tabId: action.tabId,
        workspace: action.workspace,
        isFetchingTabContent: true,
        formIds: [],
        forms: [],
        isMultipleApplyOpened: false,
        selectedFieldsIds: [],
        isProductValidated: false,
        fields: {},
        updateActions: {},
        isDeleteFormConfirmationModalOpen: false,
      });
    case 'WORKSPACE_TAB_CONTENT_FETCHED':
      if (action.workspace !== 'newBatch') {
        return state;
      }
      const content = action.tabContent as BatchPersistedTabContent;
      return patchKey(state, action.tabId, () => ({
        formIds: [],
        forms: [],
        isFetchingTabContent: false,
        productSubtype: content.productSubtype,
        productType: content.productType,
        wrapper: content.wrapper,
        isProductValidated: true,
      }));
    case 'WORKSPACE_TAB_CONTENT_FETCH_FAILED':
      if (action.workspace !== 'newBatch') {
        return state;
      }
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        isFetchingTabContent: false,
      }));
    case 'WORKSPACE_RESET_TAB_COMPLETED':
      if (action.workspace !== 'newBatch') {
        return state;
      }
      return removeKey(
        addOrReplaceKey(state, action.newId, {
          tabId: action.newId,
          workspace: action.workspace,
          isFetchingTabContent: false,
          formIds: [],
          forms: [],
          isMultipleApplyOpened: false,
          selectedFieldsIds: [],
          isProductValidated: false,
          fields: {},
          updateActions: {},
          isDeleteFormConfirmationModalOpen: false,
        }),
        action.id,
      );
    case 'NEW_BATCH_WORKSPACE_PRODUCT_TYPE_SELECTED':
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productType: action.productType,
      }));
    case 'NEW_BATCH_WORKSPACE_PRODUCT_SUBTYPE_SELECTED':
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productSubtype: action.productSubType,
      }));
    case 'NEW_BATCH_WORKSPACE_WRAPPER_SELECTED':
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        wrapper: action.wrapper,
      }));
    case 'WORKSPACE_TAB_PRODUCT_VARIATION_SELECTED':
      if (!state[action.tabId] || action.workspaceType !== 'newBatch') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productType: action.productVariation.productType,
        productSubtype: action.productVariation.productSubType,
        wrapper: action.productVariation.wrapper,
      }));
    case 'NEW_BATCH_WORKSPACE_PRODUCT_SELECTED':
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productType: action.productType,
        productSubtype: action.productSubType,
        wrapper: action.wrapper,
        isProductValidated: true,
      }));
    case 'NEW_BATCH_WORKSPACE_TOGGLE_MULTIPLE_APPLY':
      return patchKey(state, action.tabId, s => ({
        ...s,
        isMultipleApplyOpened: action.isOpen,
      }));
    case 'BATCH_MULTIPLE_APPLY_SET_SELECTED_FIELDS':
      return patchKey(state, action.tabId, s => {
        const newState = {
          ...s,
          selectedFieldsIds: action.selectedFieldsIds,
        };
        const res = action.selectedFieldsIds.reduce(
          (acc, field) => ({
            ...acc,
            fields: {
              ...acc.fields,
              [field]: newState.fields[field],
            },
            updateActions: {
              ...acc.updateActions,
              [field]: newState.updateActions[field] ?? 'replace',
            },
          }),
          { fields: {}, updateActions: {} } as Pick<BatchWorkspaceTabContent, 'fields' | 'updateActions'>,
        );
        return { ...newState, fields: res.fields ?? {}, updateActions: res.updateActions };
      });
    case 'BATCH_MULTIPLE_APPLY_SET_FIELD_VALUE':
      return patchKey(state, action.tabId, s => ({
        ...s,
        fields: { ...s.fields, [action.fieldId]: action.value },
      }));
    case 'BATCH_MULTIPLE_APPLY_CLEAR_FIELD_VALUE':
      return patchKey(state, action.tabId, s => ({
        ...s,
        fields: {},
      }));
    case 'BATCH_MULTIPLE_APPLY_SET_FIELD_ACTION':
      return patchKey(state, action.tabId, s => ({
        ...s,
        updateActions: { ...s.updateActions, [action.fieldId]: action.value },
      }));
    case 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED':
      if (!state[action.tabId]) {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        productSubtype: action.productVariation.productSubType,
        productType: action.productVariation.productType,
        wrapper: action.productVariation.wrapper,
        payoffType: action.productVariation.payoffType,
        isProductValidated: true,
      }));
    case 'DEFAULT_BATCH_FORM_REQUESTED':
    case 'BATCH_IMPORT_FORM_REQUESTED':
      return addFormIdsToTab(
        state,
        action.tabId,
        [action.temporaryFormId],
        [{ negotiationId: action.temporaryFormId }],
      );
    case 'BATCH_FORM_RELOAD_REQUESTED':
      const externalQuoteId = getExternalQuoteIdFromReloadRequest(action.identifier, action.idType);
      return addFormIdsToTab(
        state,
        action.tabId,
        [action.temporaryFormId],
        [{ negotiationId: action.temporaryFormId, quoteId: externalQuoteId }],
      );
    case 'BATCH_FORMS_RESTORE_REQUESTED':
      return addFormIdsToTab(state, action.tabId, action.formIds, action.forms);
    case 'BATCH_QUOTE_ACKED':
      return updateQuoteId(state, action.tabId, action.forms);
    case 'BATCH_QUOTE_RECEIVED':
      return updateQuoteId(state, action.tabId, [
        {
          negotiationId: action.quote.negotiationId,
          quoteId: action.quote.quoteId,
          pricerRfqId: action.quote.quoteDetails?.PricerRfqId,
        },
      ]);
    case 'BATCH_PREVIOUS_QUOTE_LOADED':
      return updateQuoteId(state, findTabIdByFormId(state, action.formId), [
        {
          negotiationId: action.formId,
          quoteId: action.quoteId,
          pricerRfqId: action.quote.quoteDetails?.PricerRfqId,
        },
      ]);
    case 'BATCH_QUOTE_REMOVED':
      return updateQuoteId(
        state,
        action.tabId,
        action.formIds.map(id => ({ negotiationId: id, quoteId: undefined, pricerRfqId: undefined })),
      );
    case 'DEFAULT_BATCH_FORM_RECEIVED':
      return replaceFormIdInTab(state, action.tabId, action.temporaryFormId, action.negotiationId);
    case 'BATCH_FORMS_DELETED':
      return patchKey(state, action.tabId, tabContent => ({
        formIds: tabContent.formIds.filter(formId => !action.formIds.includes(formId)),
        forms: tabContent.forms.filter(form => !action.formIds.includes(form.negotiationId)),
      }));
    case 'BATCH_FORMS_CLONE_REQUESTED':
      return addFormIdsToTab(
        state,
        action.tabId,
        action.temporaryFormIds,
        action.temporaryFormIds.map(id => ({ negotiationId: id })),
      );
    case 'BATCH_FORM_QUOTE_RELOAD_REQUESTED':
      return addFormIdsToTab(
        state,
        action.tabId,
        [action.temporaryFormId],
        [{ negotiationId: action.temporaryFormId, quoteId: action.quoteId }],
      );
    case 'BATCH_TOGGLE_DELETE_FORM_CONFIRMATION_MODAL':
      return patchKey(state, action.tabId, s => ({
        ...s,
        isDeleteFormConfirmationModalOpen: action.isOpen,
      }));
    default:
      return state;
  }
}

function getExternalQuoteIdFromReloadRequest(identifier: string, idType: IdType): string | undefined {
  return idType === IdType.ExternalQuoteId || isExternalQuoteId(identifier) ? identifier : undefined;
}

function addFormIdsToTab(
  state: BatchWorkspaceTabContentState,
  tabId: string,
  formIds: readonly string[],
  forms: readonly BatchTabContentFormDetails[],
): BatchWorkspaceTabContentState {
  return patchKey(state, tabId, tabContent => ({
    formIds: addElements(tabContent.formIds, ...formIds),
    forms: [...tabContent.forms, ...forms],
  }));
}

function replaceFormIdInTab(
  state: BatchWorkspaceTabContentState,
  tabId: string,
  oldFormId: string,
  newFormId: string,
): BatchWorkspaceTabContentState {
  if (oldFormId === newFormId) {
    return state;
  }
  return patchKey(state, tabId, tabContent => ({
    formIds: replaceElements(tabContent.formIds, oldFormId, newFormId),
    forms: tabContent.forms.map(form =>
      form.negotiationId === oldFormId ? { ...form, negotiationId: newFormId } : form,
    ),
  }));
}

function updateQuoteId(
  state: BatchWorkspaceTabContentState,
  tabId: string | undefined,
  forms: readonly BatchTabContentFormDetails[],
): BatchWorkspaceTabContentState {
  if (tabId === undefined) {
    return state;
  }
  return patchKey(state, tabId, tabContent => ({
    ...tabContent,
    forms: tabContent.forms.map(tabContentForm => {
      const found = forms.find(form => form.negotiationId === tabContentForm.negotiationId);
      return {
        ...tabContentForm,
        quoteId: found ? found.quoteId : tabContentForm.quoteId,
        pricerRfqId: found ? found.pricerRfqId : tabContentForm.pricerRfqId,
      };
    }),
  }));
}

function findTabIdByFormId(state: BatchWorkspaceTabContentState, formId: string): string | undefined {
  return Object.values(state).find(tabContent => tabContent.forms.some(form => form.negotiationId === formId))?.tabId;
}
